import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  FiFacebook,
  FiInstagram,
  FaWhatsapp,
  FiTwitter,
  FiYoutube,
} from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { deleteSite, setSite } from '../../../features/site/siteSlice';
import { updateSite } from '../../../api/siteApi';

function SocialLinks({ isEdit, prevClicked }) {
  const { user } = useSelector(state => state.user);
  const { site } = useSelector(state => state.site);
  const dispatch = useDispatch();

  let navigate = useNavigate();

  const [socialLinks, setSocialLinks] = useState(getEmptySocialLinks());
  const [inProgress, setInProgress] = useState(false);
  const [finishInProgress, setFinishInProgress] = useState(false);
  const [displayFinish, setDisplayFinish] = useState(isEdit);
  const [actionStatus, setActionStatus] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (isEdit) {
      setupComponent();
    }
    // eslint-disable-next-line
  }, []);

  const setupComponent = () => {
    let linksFromAPI = site?.socialLinks ?? getEmptySocialLinks();
    for (let i = 0; i < linksFromAPI.length; i++) {
      socialLinks[i].link = linksFromAPI[i].link;
    }
  };

  function getEmptySocialLinks() {
    return [
      { name: 'Twitter', link: '', icon: <FiTwitter color="gray.500" /> },
      { name: 'Facebook', link: '', icon: <FiFacebook color="gray.500" /> },
      { name: 'Instagram', link: '', icon: <FiInstagram color="gray.500" /> },
      { name: 'WhatsApp', link: '', icon: <FaWhatsapp color="gray.500" /> },
      { name: 'Youtube', link: '', icon: <FiYoutube color="gray.500" /> },
    ];
  }

  const handleChangeLink = (index, event) => {
    const values = [...socialLinks];
    values[index].link = event.target.value;
    setSocialLinks(values);
  };

  const handleFinish = () => {
    if (isEdit) {
      dispatch(deleteSite());
      navigate(`/?id=${user._id}`);
      return;
    }
    setFinishInProgress(true);

    const updateObj = {
      status: 'DRAFT',
      id: site._id,
      creator: user._id,
      updateType: 'STATUS',
    };

    updateSite(updateObj)
      .then(response => {
        setFinishInProgress(false);
        if (response.site) {
          dispatch(deleteSite());
          navigate(`/?id=${user._id}`);
        } else {
          toast({
            title: 'Error',
            description: response.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        setFinishInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const addSocialLinks = () => {
    let finalLinks = [];
    socialLinks.forEach(link => {
      finalLinks.push({
        name: link.name,
        link: link.link,
      });
    });
    setInProgress(true);
    const payload = {
      socialLinks: finalLinks,
      id: site._id,
      updateType: 'SOCIAL_LINKS',
      creator: user._id,
    };
    updateSite(payload)
      .then(response => {
        setInProgress(false);
        if (response.site) {
          if (isEdit) {
            dispatch(setSite(response.site));
          }
          let message = isEdit
            ? 'Social links updated successfully.'
            : 'Social links added successfully, Please click finish to complete the process.';
          toast({
            title: 'Success',
            description: message,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setActionStatus(`${message}`);
          setDisplayFinish(true);
        } else {
          toast({
            title: 'Error',
            description: response.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        setInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  return (
    <Card bg={useColorModeValue('white', 'gray.700')}>
      <CardBody>
        <Stack spacing={2} mt={2}>
          <FormLabel>Social Media Links</FormLabel>
          {socialLinks.map((field, idx) => {
            return (
              <InputGroup key={`${field}-${idx}`}>
                <InputLeftElement pointerEvents="none" children={field.icon} />
                <Input
                  type="text"
                  name="text"
                  placeholder={field.name}
                  value={field.link}
                  onChange={event => handleChangeLink(idx, event)}
                />
              </InputGroup>
            );
          })}
        </Stack>
      </CardBody>
      <CardFooter>
        <ButtonGroup w={'100%'} justifyContent="end">
          <Button colorScheme="teal" size="md" onClick={prevClicked}>
            Previous
          </Button>
          <Button
            colorScheme="teal"
            isLoading={inProgress}
            loadingText="Submitting..."
            disabled={inProgress || actionStatus}
            size="md"
            onClick={addSocialLinks}
            whiteSpace="normal"
          >
            {isEdit ? 'Update' : 'Add'}
          </Button>
          <Button
            colorScheme="teal"
            size="md"
            isLoading={finishInProgress}
            loadingText="Finishing..."
            display={displayFinish ? 'inherit' : 'none'}
            onClick={handleFinish}
          >
            {' '}
            {isEdit ? 'Go to home' : 'Finish'}
          </Button>
        </ButtonGroup>
      </CardFooter>
    </Card>
  );
}

export default SocialLinks;
